import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  CaretLeft, 
  CreditCard, 
  ShieldCheck, 
  Lock, 
  CheckCircle,
  SpinnerGap,
  Coffee,
  Leaf,
  ForkKnife
} from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════
// COLORS
// ═══════════════════════════════════════════════════════════
const COLORS = {
  bg: "#F5E6E0",
  green: "#2D5A3D",
  orange: "#E07A3D",
  tan: "#D4A574",
  text: "#1A1A1A",
  textMuted: "#6B5B4F",
  white: "#FFFFFF",
};

// ═══════════════════════════════════════════════════════════
// MEMBERSHIP PLANS - MUST MATCH BACKEND PRICES!
// ═══════════════════════════════════════════════════════════
const PLANS = {
  matcha: {
    id: "matcha",
    name: "Franky's Matcha Club",
    price: 29.90,
    color: "#4A7C59",
    icon: Leaf,
    features: [
      "1 Basic Matcha pro Woche gratis",
      "10% Rabatt auf alle Speisen & Drinks",
      "Geburtstagsgeschenk",
      "In allen Filialen einlösbar",
    ],
    note: "Upgrades (Hafermilch, Sirup) nicht inklusive – gegen kleinen Aufpreis.",
    badge: "BESTSELLER",
  },
  coffee: {
    id: "coffee",
    name: "Franky's Coffee Pass",
    price: 19.90,
    color: COLORS.tan,
    icon: Coffee,
    features: [
      "1 Basic Coffee jeden Tag",
      "Espresso, Americano, Cappuccino oder kleiner Latte",
      "Standardmilch inklusive",
      "In allen Filialen einlösbar",
    ],
    note: "Alternative Milch & Sirup gegen kleinen Aufpreis.",
  },
  pasta: {
    id: "pasta",
    name: "Franky's Pasta Flat",
    price: 32.99,
    color: COLORS.green,
    icon: ForkKnife,
    features: [
      "4 Pasta-Gerichte pro Monat gratis",
      "Egal welche Pasta von der Karte",
      "Frei im Monat einlösbar – wann du willst",
      "In unserer Aachener Filiale einlösbar",
    ],
    note: "Extra-Toppings & Premium-Pasta gegen kleinen Aufpreis.",
  },
};

// ═══════════════════════════════════════════════════════════
// SUCCESS PAGE COMPONENT
// ═══════════════════════════════════════════════════════════
const SuccessPage = ({ plan }) => {
  const navigate = useNavigate();
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="min-h-screen flex flex-col items-center justify-center p-6"
      style={{ backgroundColor: COLORS.green }}
    >
      {/* Confetti-like particles */}
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-3 h-3 rounded-full"
          style={{ 
            backgroundColor: i % 3 === 0 ? "#FFD700" : i % 3 === 1 ? COLORS.orange : "#FF6B6B",
            left: `${10 + (i * 7)}%`,
          }}
          animate={{ 
            y: ["0vh", "100vh"],
            opacity: [0, 1, 1, 0],
            rotate: [0, 360],
          }}
          transition={{ 
            duration: 3,
            delay: i * 0.2,
            repeat: Infinity,
          }}
        />
      ))}
      
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", delay: 0.2 }}
        className="w-24 h-24 rounded-full flex items-center justify-center mb-6"
        style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
      >
        <CheckCircle size={60} weight="fill" className="text-white" />
      </motion.div>
      
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-3xl font-black text-white text-center mb-2"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        Willkommen im Club!
      </motion.h1>
      
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-white/80 text-center mb-8"
        style={{ fontFamily: "'Figtree', sans-serif" }}
      >
        Dein {plan?.name || "Membership"} ist jetzt aktiv!
      </motion.p>
      
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate("/landing")}
        className="px-8 py-4 rounded-2xl font-bold"
        style={{ 
          backgroundColor: COLORS.white,
          color: COLORS.green,
          fontFamily: "'Space Grotesk', sans-serif"
        }}
      >
        Zur App
      </motion.button>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// PROCESSING/POLLING COMPONENT
// ═══════════════════════════════════════════════════════════
const ProcessingPage = ({ message }) => (
  <div 
    className="min-h-screen flex flex-col items-center justify-center p-6"
    style={{ backgroundColor: COLORS.bg }}
  >
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      className="mb-6"
    >
      <SpinnerGap size={48} weight="bold" style={{ color: COLORS.green }} />
    </motion.div>
    <p 
      className="text-lg font-bold text-center"
      style={{ color: COLORS.text, fontFamily: "'Space Grotesk', sans-serif" }}
    >
      {message || "Einen Moment bitte..."}
    </p>
  </div>
);

// ═══════════════════════════════════════════════════════════
// MAIN CHECKOUT PAGE
// ═══════════════════════════════════════════════════════════
export default function MembershipCheckoutPage() {
  const { planId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token, setShowAuthModal } = useAuth();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  const plan = PLANS[planId];
  const sessionId = searchParams.get("session_id");

  // Poll for payment status if returning from Stripe
  useEffect(() => {
    if (sessionId && !paymentSuccess) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Zahlung konnte nicht bestätigt werden. Bitte kontaktiere uns.");
      setIsPolling(false);
      return;
    }

    setIsPolling(true);

    try {
      const response = await axios.get(`${API}/api/membership/status/${sid}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setPaymentSuccess(true);
        setIsPolling(false);
        toast.success("Zahlung erfolgreich!");
        return;
      } else if (data.status === "expired") {
        toast.error("Zahlungssession abgelaufen. Bitte versuche es erneut.");
        setIsPolling(false);
        navigate("/membership");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment:", error);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      setShowAuthModal(true);
      toast.error("Bitte melde dich zuerst an");
      return;
    }

    if (!acceptTerms) {
      toast.error("Bitte akzeptiere die Bedingungen");
      return;
    }

    setIsProcessing(true);

    try {
      const response = await axios.post(
        `${API}/api/membership/checkout`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("Keine Checkout-URL erhalten");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Checkout");
      setIsProcessing(false);
    }
  };

  // Show success page
  if (paymentSuccess) {
    return <SuccessPage plan={plan} />;
  }

  // Show polling page
  if (isPolling) {
    return <ProcessingPage message="Zahlung wird überprüft..." />;
  }

  // Show processing page
  if (isProcessing) {
    return <ProcessingPage message="Weiterleitung zu Stripe..." />;
  }

  // Invalid plan
  if (!plan) {
    navigate("/membership");
    return null;
  }

  const Icon = plan.icon;

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: COLORS.bg }}>
      {/* Header */}
      <header className="px-4 pt-6 pb-4 flex items-center gap-3">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => navigate("/membership")}
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: COLORS.white }}
        >
          <CaretLeft size={22} weight="bold" style={{ color: COLORS.text }} />
        </motion.button>
        <div>
          <h1 
            className="text-xl font-black"
            style={{ color: COLORS.text, fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Checkout
          </h1>
          <p className="text-sm" style={{ color: COLORS.textMuted }}>
            {plan.name}
          </p>
        </div>
      </header>

      <main className="px-4 space-y-4">
        {/* Plan Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-5 rounded-3xl"
          style={{ backgroundColor: plan.color }}
        >
          <div className="flex items-center gap-4">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
            >
              <Icon size={32} weight="fill" className="text-white" />
            </div>
            <div className="flex-1">
              {plan.badge && (
                <span className="text-xs font-bold px-2 py-1 rounded-full bg-white/20 text-white mb-1 inline-block">
                  {plan.badge}
                </span>
              )}
              <h2 
                className="text-xl font-black text-white"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {plan.name}
              </h2>
              <p className="text-white/80 text-sm">{plan.price.toFixed(2)}€ / Monat</p>
            </div>
          </div>
          
          {/* Features */}
          <div className="mt-4 pt-4 border-t border-white/20">
            {plan.features.map((feature, i) => (
              <div key={i} className="flex items-center gap-2 text-white/90 text-sm py-1">
                <CheckCircle size={16} weight="fill" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Payment Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-5 rounded-3xl"
          style={{ backgroundColor: COLORS.white }}
        >
          <h3 
            className="font-bold mb-4 flex items-center gap-2"
            style={{ color: COLORS.text, fontFamily: "'Space Grotesk', sans-serif" }}
          >
            <CreditCard size={20} weight="fill" style={{ color: COLORS.green }} />
            Zahlungsdetails
          </h3>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: COLORS.bg }}>
              <span style={{ color: COLORS.textMuted }}>Monatlich</span>
              <span className="font-bold" style={{ color: COLORS.text }}>{plan.price.toFixed(2)}€</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="font-bold" style={{ color: COLORS.text }}>Heute zu zahlen</span>
              <span className="text-xl font-black" style={{ color: COLORS.green }}>{plan.price.toFixed(2)}€</span>
            </div>
          </div>

          {/* Security Badges */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t" style={{ borderColor: COLORS.bg }}>
            <div className="flex items-center gap-1 text-xs" style={{ color: COLORS.textMuted }}>
              <Lock size={14} weight="fill" />
              <span>SSL Verschlüsselt</span>
            </div>
            <div className="flex items-center gap-1 text-xs" style={{ color: COLORS.textMuted }}>
              <ShieldCheck size={14} weight="fill" />
              <span>Powered by Stripe</span>
            </div>
          </div>
        </motion.div>

        {/* Terms */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-4 rounded-2xl"
          style={{ backgroundColor: COLORS.white }}
        >
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="w-5 h-5 rounded mt-0.5"
              style={{ accentColor: COLORS.green }}
            />
            <span className="text-sm" style={{ color: COLORS.textMuted }}>
              Ich akzeptiere die <a href="#" className="underline" style={{ color: COLORS.green }}>AGB</a> und{" "}
              <a href="#" className="underline" style={{ color: COLORS.green }}>Datenschutz</a>.
              Das Abo verlängert sich automatisch um je 1 Monat. Kündigungsfrist: 3 Monate – jederzeit im Profil möglich.
            </span>
          </label>
        </motion.div>

        {/* Checkout Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleCheckout}
          disabled={!acceptTerms || isProcessing}
          className="w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50"
          style={{ 
            backgroundColor: COLORS.green,
            color: COLORS.white,
            fontFamily: "'Space Grotesk', sans-serif",
            boxShadow: `0 8px 24px ${COLORS.green}40`
          }}
        >
          <CreditCard size={22} weight="fill" />
          <span>Jetzt bezahlen - {plan.price.toFixed(2)}€</span>
        </motion.button>

        {/* Cancel Info */}
        <p 
          className="text-center text-xs"
          style={{ color: COLORS.textMuted }}
        >
          3 Monate Kündigungsfrist · Keine versteckten Kosten
        </p>
      </main>
    </div>
  );
}
